import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PasswordPolicyComponent } from '../password-policy/password-policy.component';
import { SecurityPolicy } from 'src/app/models/security-policy';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityPolicyService } from 'src/app/services/security-policy-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { ExternalsParametersService } from 'src/app/services/externals-parameters.service';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { Subscription, timeout } from 'rxjs';
import { ExternalResponse } from 'src/app/models/external-response';

@Component({
    selector: 'app-changepassword',
    imports: [
        MatCardModule,
        TranslateModule,
        ReactiveFormsModule,
        MatFormField,
        MatIcon,
        MatLabel,
        MatInput,
        MatError,
        MatIconButton,
        MatSuffix,
        MatButton,
        PasswordPolicyComponent,
    ],
    templateUrl: './changepassword.component.html',
    styleUrls: ['./changepassword.component.css']
})
export class ChangepasswordComponent implements OnInit, OnDestroy {
    changePasswordForm: UntypedFormGroup;
    hideSecurityCode = true;
    hideNewPassword = true;
    hideNewConfirmedPassword = true;

    securityPolicy: SecurityPolicy;
    userId: string;

    externalParameter: ExternalParameters;
    externalParametersChange: Subscription;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private securityPolicyService: SecurityPolicyService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private externalsParametersService: ExternalsParametersService
    ) {}

    ngOnDestroy(): void {
        this.externalsParametersService.resetExternalParameters();
        this.externalParametersChange.unsubscribe();
    }

    ngOnInit() {
        this.externalParametersChange = this.externalsParametersService.externalParametersChange$.subscribe((parameters) => {
            if (parameters) {
                this.externalParameter = parameters;
            }
        });

        this.route.queryParams.subscribe((params) => {
            if (params['resetPassword']) {
                this.loadDatas(params['resetPassword']);
            } else {
                this.router.navigate(['/login']);
            }
        });
    }

    onSubmit() {
        this.spinner.show();

        this.securityPolicyService
            .FinishResetPassword(this.userId, this.changePasswordForm.get('securityCode').value, this.changePasswordForm.get('newConfirmedPassword').value)
            .subscribe({
                next: (x) => {
                    this.spinner.hide();

                    if (this.externalParameter?.return) {
                        const response = new ExternalResponse();
                        response.code = 200;
                        response.message = encodeURI(this.translate.instant('ChangePassword.ChangepasswordSucces'));
                        window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                        this.externalsParametersService.resetExternalParameters();
                    } else {
                        this.toastr.success(this.translate.instant('ChangePassword.ChangepasswordSucces'));
                        this.router.navigate(['/profile']);
                    }
                },
                error: (err: unknown) => {
                    const error = err as HttpErrorResponse;

                    this.spinner.hide();

                    if (this.externalParameter?.return) {
                        const response = new ExternalResponse();
                        response.code = error.status;
                        response.message = encodeURI(this.translate.instant('ChangePassword.ChangepasswordError'));
                        window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                        this.externalsParametersService.resetExternalParameters();
                    } else if (error.status === 412) {
                        this.toastr.warning(this.translate.instant('ChangePassword.ChangepasswordError'));
                    }
                },
            });
    }

    cancel() {
        if (this.externalParameter?.return) {
            window.location.href = this.externalParameter.return;
            this.externalsParametersService.resetExternalParameters();
        } else {
            this.router.navigateByUrl('/login');
        }
    }

    private loadDatas(userId: string) {
        this.securityPolicyService.GetSecurityPolicy(userId, false).subscribe({
            next: (securityPolicy) => {
                this.userId = userId;
                this.securityPolicy = securityPolicy;

                this.changePasswordForm = this.formBuilder.group({
                    securityCode: ['', [Validators.required, Validators.pattern(/^\d{8}$/)]],
                    newPassword: ['', [Validators.required, this.passwordValidators(this.securityPolicy)]],
                    newConfirmedPassword: ['', [Validators.required, this.passwordValidators(this.securityPolicy)]],
                });
            },
            error: () => {
                this.securityPolicy = new SecurityPolicy();
                this.securityPolicy.passwordHistoryCount = 3;
                this.securityPolicy.passwordMaxCasesLength = 20;
                this.securityPolicy.passwordMinCasesLength = 12;
                this.securityPolicy.passwordMinDigitsLength = 2;
                this.securityPolicy.passwordMinLowerCasesLength = 2;
                this.securityPolicy.passwordMinSpecialCasesLength = 2;
                this.securityPolicy.passwordMinUpperCasesLength = 2;
                this.securityPolicy.passwordSpecialCases = '$&@%?#"';

                this.changePasswordForm = this.formBuilder.group({
                    securityCode: ['', [Validators.required, Validators.pattern(/^\d{8}$/)]],
                    newPassword: ['', [Validators.required, this.passwordValidators(this.securityPolicy)]],
                    newConfirmedPassword: ['', [Validators.required, this.passwordValidators(this.securityPolicy)]],
                });
            },
        });
    }

    passwordValidators(securityPolicy: SecurityPolicy): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};
            const controlValue = String(control.value);

            if (controlValue && securityPolicy) {
                // Controle du nombre de caractères
                if (!new RegExp(`^.{${securityPolicy.passwordMinCasesLength},${securityPolicy.passwordMaxCasesLength}}$`).test(control.value)) {
                    validation['passwordLength'] = true;
                    return validation;
                }

                // contôle du nombre de majuscules
                if (!new RegExp(`(?=(?:[^A-Z]*[A-Z]){${securityPolicy.passwordMinUpperCasesLength}})`).test(control.value)) {
                    validation['minUpperCasesLength'] = true;
                    return validation;
                }

                // contôle du nombre de minuscules
                if (!new RegExp(`(?=(?:[^a-z]*[a-z]){${securityPolicy.passwordMinLowerCasesLength}})`).test(control.value)) {
                    validation['minLowerCasesLength'] = true;
                    return validation;
                }

                // contôle du nombre de chiffres
                if (!new RegExp(`(?=(?:\\D*\\d){${securityPolicy.passwordMinDigitsLength}})`).test(control.value)) {
                    validation['minDigitsLength'] = true;
                    return validation;
                }

                if (this.CheckSpecialCharacters(securityPolicy, control.value)) {
                    validation['minSpecialCasesLength'] = true;
                    return validation;
                }
            }

            return validation;
        };
    }

    private CheckSpecialCharacters(securityPolicy: SecurityPolicy, input: string): boolean {
        let specialCharactersList: string = '';

        // Echappe tous les caractères de la liste
        for (let index = 0; index < securityPolicy.passwordSpecialCases.length; index++) {
            specialCharactersList += `\\${securityPolicy.passwordSpecialCases[index]}`;
        }

        const regexSpecialCharacters = new RegExp(`[${specialCharactersList}]`, 'g');

        return (input.match(regexSpecialCharacters) || []).length < securityPolicy.passwordMinSpecialCasesLength;
    }
}
